import {
  Metadata,
  PublicationSettingSearchableContentType,
  StructureInput,
  UiModuleInput
} from 'data/generated/graphql';
import { EditorLayout } from './layoutUtils';
import { makeEnumNormalizer } from './objectUtils';

const METADATA_BY_LOWER = new Map<string, Metadata>(Object.values(Metadata).map((v) => [v.toLowerCase(), v]));
const CONTENT_TYPE_BY_LOWER = new Map<string, PublicationSettingSearchableContentType>(
  Object.values(PublicationSettingSearchableContentType).map((v) => [v.toLowerCase(), v])
);
const normMeta = makeEnumNormalizer(METADATA_BY_LOWER);
const normContentType = makeEnumNormalizer(CONTENT_TYPE_BY_LOWER);

type NormalizableModule = {
  allowedMeta?: Metadata[] | null;
  contentTypes?: PublicationSettingSearchableContentType[] | null;
};
function normModule<T extends NormalizableModule>(mod: T): T {
  // Strip null/undefined values — they mean "no override" and GraphQL non-null fields reject them.
  const stripped = Object.fromEntries(Object.entries(mod).filter(([, v]) => v != null)) as T;
  return {
    ...stripped,
    ...(stripped.allowedMeta?.length && { allowedMeta: stripped.allowedMeta.map(normMeta) }),
    ...(stripped.contentTypes != null && { contentTypes: stripped.contentTypes.map(normContentType) })
  };
}

// Normalizes all present module sub-fields — no hardcoded list needed.
// normModule is safe on any module type: it only transforms fields that exist on the module,
// so module types without allowedMeta / contentTypes are passed through unchanged.
function normalizeUiModuleFields(fields: UiModuleInput): UiModuleInput {
  const overrides = Object.fromEntries(
    Object.entries(fields)
      .filter(([k, v]) => k !== '__typename' && v != null)
      .map(([k, v]) => [k, normModule(v as NormalizableModule)])
  );
  return { ...fields, ...overrides };
}

// Produces the structure for storage in the layout artifact.
// The editor state already uses structure natively; this normalises enum-cased uiModuleFields and
// recomputes slotRefs from occurrence order (placeholder refs used during editing are replaced).
const computeStructure = (layout: EditorLayout): StructureInput => ({
  groups: (layout.structure?.groups ?? []).map((group) => {
    const ttCounter = new Map<string, number>();
    const slots = group.modules.map((m) => {
      const idx = ttCounter.get(m.ttKey) ?? 0;
      ttCounter.set(m.ttKey, idx + 1);
      const slotRef = `${m.ttKey}-${idx}`;

      let uiModuleFields = m.uiModuleFields ? normalizeUiModuleFields(m.uiModuleFields as UiModuleInput) : undefined;
      if (uiModuleFields) {
        // StructureSlotInput.uiModuleFields uses UiModuleInput, whose sub-types (UiTitleModuleInput etc.)
        // all require treatmentType. Slot overrides only store diffed values so treatmentType is typically
        // absent — inject from ttKey so GraphQL input validation passes.
        uiModuleFields = Object.fromEntries(
          Object.entries(uiModuleFields).map(([k, v]) => {
            if (v && typeof v === 'object' && !Array.isArray(v) && !('treatmentType' in v)) {
              return [k, { treatmentType: m.ttKey, ...(v as object) }];
            }
            return [k, v];
          })
        ) as UiModuleInput;
      }

      return { ...m, slotRef, uiModuleFields };
    });
    return { ...group, modules: slots };
  })
});

// Strips runtime state from a Layout and produces the GQL publish body.
export const serializeLayoutForPublish = (layout: EditorLayout) => ({
  id: layout.id,
  name: layout.name,
  contentTypes: layout.contentTypes ?? undefined,
  structure: computeStructure(layout),
  availableTreatmentTypes: layout.availableTreatmentTypes
});
